import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Send, Sparkles, Brain, Bot, User, Loader2, Image as ImageIcon, Camera, ChevronRight, CheckCircle2, MapPin, Map as MapIcon, Eye, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { CommanServices } from '@/services/commanServices';
import logoImg from '@/assets/Img/logo.png';
import MyGoogleMap from '@/components/MyGoogleMap';
import GuidedLocationPicker from '@/components/GuidedLocationPicker';
import AuthenticatedMedia from '@/components/AuthenticatedMedia';

const INTRO_MESSAGE_CONTENT = "Hi! I'm your AI assistant. How can I help you today?";
const INCIDENT_PROMPT_CONTENT = "What happened? Please describe the incident — include what occurred, where, and whether it is still ongoing.";

const AIChatPanel = ({ 
  isOpen, 
  onClose, 
  onApplySuggestion, 
  attachments = [], 
  currentData = {}, 
  onChatUpdate,
  locationAssistantRequested,
  onLocationAssistantHandled,
  locations = [], // Received from parent
  onNavigateToCreateLocation,
}) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: INTRO_MESSAGE_CONTENT,
      isIntro: true,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [lastAnalyzedCount, setLastAnalyzedCount] = useState(0);
  const [appliedSuggestion, setAppliedSuggestion] = useState(null);
  // Rules 47-49: Track classification and location applied states independently
  const [classificationApplied, setClassificationApplied] = useState(false);
  const [locationApplied, setLocationApplied] = useState(false);
  const [gpsData, setGpsData] = useState(null);
  const [previewLocationId, setPreviewLocationId] = useState(null);
  const [guidedSearch, setGuidedSearch] = useState({
    active: false,
    selectedParentId: null, // ID of the currently selected parent (null = show roots)
    breadcrumb: []          // Array of {id, type, value} representing the path from root to current parent
  });

  // Defensive: Ensure locations is always an array for the guided search
  const safeLocations = Array.isArray(locations) ? locations : (locations?.locations || locations?.zones || []);

  const buildLocationDisplayPath = (loc) => {
    if (!loc) return '';
    const parts = [];
    if (loc.ancestors?.length > 0) {
      loc.ancestors.forEach((a) => { if (a?.value) parts.push(a.value); });
    } else if (loc.parent_id) {
      const byId = new Map(safeLocations.map((l) => [l.id, l]));
      let current = loc;
      const chain = [];
      const visited = new Set();
      while (current && !visited.has(current.id)) {
        visited.add(current.id);
        chain.unshift(current.zone_name || current.auto_label || '');
        current = byId.get(current.parent_id);
      }
      return chain.filter(Boolean).join(' → ');
    }
    const name = loc.zone_name || loc.auto_label || '';
    if (name && (!parts.length || parts[parts.length - 1] !== name)) parts.push(name);
    return parts.filter(Boolean).join(' → ');
  };

  const getLocationMediaPreview = (locationData) => {
    const attachments = locationData?.attachments;
    if (!Array.isArray(attachments)) return null;
    const img = attachments.find((a) => {
      const url = typeof a === 'string' ? a : (a?.url || a?.file_url || a?.s3_url);
      const type = typeof a === 'object' ? (a?.type || a?.content_type || '') : '';
      return url && (type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(url));
    });
    if (!img) return null;
    return typeof img === 'string' ? img : (img.url || img.file_url || img.s3_url);
  };
  const scrollRef = useRef(null);

  // Handle Location Assistant Request
  useEffect(() => {
    if (locationAssistantRequested) {
      setIntroDismissed(true);
      startLocationAssistant();
      if (onLocationAssistantHandled) onLocationAssistantHandled();
    }
  }, [locationAssistantRequested]);

  // Restore default greeting if opened via general AI button and no progress made
  useEffect(() => {
    if (isOpen && !locationAssistantRequested) {
      setMessages(prev => {
        // If the only message is a location prompt that hasn't been interacted with,
        // and we opened via the general AI button, restore the default greeting.
        if (prev.length === 1 && prev[0].isLocationFlow) {
          setIntroDismissed(false);
          return [{
            id: '1',
            role: 'assistant',
            content: INTRO_MESSAGE_CONTENT,
            isIntro: true,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }];
        }
        return prev;
      });
    }
  }, [isOpen, locationAssistantRequested]);

  const handleIntroChoice = (choice) => {
    if (introDismissed || isTyping) return;
    setIntroDismissed(true);
    if (choice === 'location') {
      startLocationAssistant();
    } else if (choice === 'incident') {
      setMessages(prev => {
        if (prev.length === 1 && prev[0].isIntro) {
          return [{
            ...prev[0],
            content: INCIDENT_PROMPT_CONTENT,
            isIntro: false
          }];
        }
        return prev;
      });
    }
  };

  const startLocationAssistant = () => {
    if (isTyping) return; // Prevent multiple simultaneous triggers if user clicks rapidly
    setIsTyping(true);
    fetchGps(); // Get coordinates to help AI
    setTimeout(() => {
      const assistantMessage = {
        id: `loc-${Date.now()}`,
        role: 'assistant',
        content: "I'll help you pinpoint your location. \n\nCould you tell me which building or campus you're in? Also, are there any visible signboards, room numbers, or nearby landmarks (like 'near the cafeteria' or 'Room 402')?",
        isLocationFlow: true,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages(prev => {
        // Avoid duplicate consecutive location prompts if user taps multiple times
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.isLocationFlow && lastMsg.role === 'assistant') {
          return prev;
        }

        // If the only message is the initial intro greeting, replace it
        if (prev.length === 1 && (prev[0].id === '1' || prev[0].isIntro)) {
          return [assistantMessage];
        }
        
        return [...prev, assistantMessage];
      });
      setIsTyping(false);
    }, 600);
  };

  // Notify parent of state changes for auditing (Requirement 9)
  useEffect(() => {
    if (onChatUpdate) {
      onChatUpdate({
        messages,
        appliedSuggestion
      });
    }
  }, [messages, appliedSuggestion, onChatUpdate]);

  const [isOffline, setIsOffline] = useState(!window.navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Filter only images from attachments
  const imageAttachments = attachments.filter(file => file && file.type && file.type.startsWith('image/'));

  useEffect(() => {
    // Reset analyzed count if all attachments are removed
    if (imageAttachments.length === 0) {
      setLastAnalyzedCount(0);
    }
  }, [imageAttachments.length]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };
    
    // Scroll immediately
    scrollToBottom();
    
    // And also after a short delay to account for rendering/images loading
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages, isTyping, guidedSearch.active, guidedSearch.selectedParentId, imageAttachments.length]);

  const fetchGps = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsData({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          console.warn("GPS Error:", error.message);
        }
      );
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSend = async (customInput = null, imagePayload = null) => {
    const textToSend = customInput !== null ? customInput : input;
    if (!textToSend.trim() && !imagePayload && imageAttachments.length === 0) return;
    if (isTyping) return;

    if (!introDismissed) setIntroDismissed(true);

    setIsTyping(true);

    let finalImagePayload = imagePayload;
    
    // Auto-include images if there are any and they haven't been sent/analyzed yet
    if (!finalImagePayload && imageAttachments.length > 0 && lastAnalyzedCount < imageAttachments.length) {
      try {
        finalImagePayload = await Promise.all(imageAttachments.map(async (file) => {
          const base64 = await fileToBase64(file);
          return { url: base64, name: file.name };
        }));
        setLastAnalyzedCount(imageAttachments.length);
      } catch (error) {
        console.error("Error auto-preparing images:", error);
      }
    }

    const messageId = Date.now().toString();
    // isLocationTurn: true when classification is done but location still missing —
    // these turns count toward the guided-search escalation threshold.
    const isInLocationPhase = !!(currentData?.classification_complete || currentData?.type_id)
      && !currentData?.has_location;
    const userMessage = {
      id: messageId,
      role: 'user',
      content: textToSend,
      images: finalImagePayload ? finalImagePayload.map(img => img.url) : null,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isLocationTurn: isInLocationPhase
    };

    setMessages(prev => [...prev, userMessage]);
    if (customInput === null) setInput('');

    // Detect location assistant trigger phrases
    const locationPhrases = ["i don't know where i am", "i'm lost", "where am i", "don't know location", "help me find my location", "unknown location"];
    const isLocationTrigger = locationPhrases.some(phrase => textToSend.toLowerCase().includes(phrase));

    if (isLocationTrigger) {
      startLocationAssistant();
      return;
    }

    if (imagePayload) {
      setLastAnalyzedCount(imageAttachments.length);
    }

    try {
      // Prepare messages for API
      const apiMessages = [...messages, userMessage].map(m => {
        if (m.id === messageId && finalImagePayload) {
          // Special vision payload for the current message
          const contentItems = [{ type: "text", text: m.content || "Analyze these images" }];
          finalImagePayload.forEach(img => {
            contentItems.push({
              type: "image_url",
              image_url: { url: img.url }
            });
          });
          return { role: m.role, content: contentItems };
        }
        return { role: m.role, content: m.content };
      });

      // Rules 40-41: Count location difficulty turns from conversation history.
      // This works independently of location_assistant_mode flag.
      // A "location turn" = classification is complete + location not yet applied + user sent a message.
      const classificationDone = !!(currentData?.classification_complete || currentData?.type_id);
      const locationMissing = !currentData?.has_location;
      const locationTurnCount = classificationDone && locationMissing
        ? messages.filter(m => m.role === 'user' && m.isLocationTurn).length + 1
        : 0;
      const LOCATION_ESCALATION_THRESHOLD = 3; // auto-activate guided search after 3 failed location turns

      const response = await CommanServices.getAiChatResponse({
        messages: apiMessages,
        context: {
          ...currentData,
          gps: gpsData,
          user_message_count: messages.filter(m => m.role === 'user').length + 1,
          location_attempt_count: locationTurnCount,
          location_assistant_mode: classificationDone && locationMissing,
          classification_applied: classificationApplied,
          location_applied: locationApplied,
          guided_parent_id: guidedSearch.selectedParentId,
          guided_breadcrumb: guidedSearch.breadcrumb,
          location_assistant_requirements: (
            'Use LocationsAndZones hierarchy: match parent nodes first, then children. ' +
            'Never flatten — always return full path with → separator. ' +
            'Include description and media references when available.'
          ),
        }
      });

      if (response.status && response.data) {
        const aiResponse = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.data.content,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        const suggestion = parseSuggestion(response.data.content);

        // Trigger 1: Backend explicitly set cannotMatch: true
        const backendSaysCannotMatch = suggestion?.cannotMatch === true;

        // Trigger 2: Conversation has had enough failed location turns (frontend safety net)
        const locationStuck = classificationDone && locationMissing
          && locationTurnCount >= LOCATION_ESCALATION_THRESHOLD;

        // Trigger 3: AI returned no good location candidates after a couple turns
        const hasGoodLocationMatch = suggestion?.locationSuggestions?.some(
          loc => loc.confidence === 'High' || loc.confidence === 'Medium'
        );
        const aiCannotLocate = classificationDone && locationMissing
          && locationTurnCount >= 2
          && !hasGoodLocationMatch
          && (suggestion?.locationSuggestions?.length === 0 || !suggestion?.locationSuggestions);

        if ((backendSaysCannotMatch || locationStuck || aiCannotLocate) && !guidedSearch.active) {
          setGuidedSearch(prev => ({ ...prev, active: true, selectedParentId: null, breadcrumb: [] }));
        }

        setMessages(prev => [...prev, aiResponse]);
      } else {
        throw new Error(response.message || 'Failed to get AI response');
      }
    } catch (error) {
      console.error('AI Chat Error:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I'm sorry, I encountered an error: ${error.message || 'Unknown error'}. Please try again.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleAnalyzeImages = async () => {
    if (imageAttachments.length === 0) return;
    
    setIsTyping(true);
    try {
      const base64Images = await Promise.all(imageAttachments.map(async (file) => {
        const base64 = await fileToBase64(file);
        return { url: base64, name: file.name };
      }));

      handleSend("Please analyze these attached photos and describe what you see.", base64Images);
    } catch (error) {
      console.error("Error preparing images:", error);
      setIsTyping(false);
    }
  };

  const parseSuggestion = (text) => {
    const regex = /\[SUGGESTION\]\s*(\{.*?\})\s*\[\/SUGGESTION\]/s;
    const match = text.match(regex);
    if (match && match[1]) {
      try {
        return JSON.parse(match[1]);
      } catch (e) {
        console.error("Failed to parse AI suggestion:", e);
        return null;
      }
    }
    return null;
  };

  const handleApplySuggestion = (suggestion) => {
    setAppliedSuggestion(suggestion);
    if (onApplySuggestion) {
      onApplySuggestion(suggestion);
    }

    // Rules 47-49: Track which field was just applied independently
    const hasClassification = !!(suggestion.incident_type && suggestion.type_id);
    const hasLocation = !!(suggestion.location_id);

    if (hasClassification) {
      setClassificationApplied(true);
    }
    if (hasLocation) {
      setLocationApplied(true);
    }

    // Rule 14–17 / Rule 45: Fire the appropriate internal trigger so the AI advances
    // to the next required step without asking for any confirmation.
    const internalTrigger = hasLocation ? '__LOCATION_APPLIED__' : '__CLASSIFICATION_APPLIED__';
    setTimeout(() => {
      handleSend(
        internalTrigger,  // internal trigger — filtered in render
        null
      );
    }, 150);
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200 shadow-2xl animate-in slide-in-from-right duration-300 w-full max-w-md">
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white" style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)' }}>
        <div className="flex items-center space-x-2">
          <div className="flex h-[38px] w-[38px] items-center justify-center rounded-lg" style={{ background: 'rgba(255,255,255,0.18)' }}>
            <Brain className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[15px] leading-none">AI Assistant</h3>
            <span className="text-[10px] text-white/80 flex items-center mt-1">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse" />
              Incident Reporting Agent
            </span>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onClose}
          className="text-white hover:bg-white/20 h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 bg-slate-50/50" viewportRef={scrollRef}>
        <div className="space-y-4">
          {messages.filter(m => m.content !== '__CLASSIFICATION_APPLIED__' && m.content !== '__LOCATION_APPLIED__').map((message) => {
            const suggestion = message.role === 'assistant' ? parseSuggestion(message.content) : null;
            const displayContent = message.content
              .replace(/\[SUGGESTION\].*?\[\/SUGGESTION\]/gs, '')
              .replace(/```json\s*```/g, '')
              .trim();

            return (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'} items-start gap-2`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                    message.role === 'user' 
                      ? 'bg-indigo-100 text-indigo-600' 
                      : 'bg-indigo-100 text-white'
                  }`}>
                    {message.role === 'user' ? <User className="h-4 w-4" /> : <img src={logoImg} alt="AI" className="h-5 w-5 object-contain" />}
                  </div>
                  <div className="flex flex-col">
                    <div className={`px-4 py-2.5 rounded-2xl shadow-sm text-sm ${
                      message.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-tr-none'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-tl-none'
                    }`}>
                      {message.role === 'user' ? (
                        displayContent
                      ) : (
                        <div className="prose prose-slate prose-sm max-w-none prose-p:leading-relaxed prose-pre:bg-slate-900 prose-pre:text-slate-100">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {displayContent}
                          </ReactMarkdown>
                        </div>
                      )}
                      {message.images && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {message.images.map((img, idx) => (
                            <img 
                              key={idx} 
                              src={img} 
                              alt="Analyzed" 
                              className="w-20 h-20 object-cover rounded-lg border border-white/20"
                            />
                          ))}
                        </div>
                      )}

                      {message.isIntro && !introDismissed && (
                        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleIntroChoice('location')}
                            disabled={isTyping}
                            className="text-xs font-medium bg-violet-50 text-violet-700 px-3 py-1.5 rounded-full border border-violet-200 hover:bg-violet-100 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <MapPin className="h-3 w-3" />
                            Location Assistance
                          </button>
                          <button
                            type="button"
                            onClick={() => handleIntroChoice('incident')}
                            disabled={isTyping}
                            className="text-xs font-medium bg-slate-50 text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-100 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <Sparkles className="h-3 w-3" />
                            Incident Assistance
                          </button>
                        </div>
                      )}
                      
                      {suggestion && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          {/* Classification Section (if present) */}
                          {(suggestion.incident_type || suggestion.event) && (
                            <>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Recommended Classification</p>
                              <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 space-y-1.5 mb-3">
                                <div className="flex justify-between items-center text-xs">
                                  <span className="text-slate-500">Type:</span>
                                  <span className="font-semibold text-slate-700">{suggestion.incident_type}</span>
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                  <span className="text-slate-500">Event:</span>
                                  <span className="font-semibold text-slate-700">{suggestion.event}</span>
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                  <span className="text-slate-500">Trigger:</span>
                                  <span className="font-semibold text-slate-700">{suggestion.trigger}</span>
                                </div>
                                <div className="flex justify-between items-center text-xs pt-1 border-t border-slate-200/50">
                                  <span className="text-slate-500">Severity:</span>
                                  <Badge variant="outline" className={`text-[9px] ${
                                    suggestion.severity?.toUpperCase() === 'HIGH' ? 'bg-red-50 text-red-600 border-red-200' :
                                    suggestion.severity?.toUpperCase() === 'MEDIUM' ? 'bg-orange-50 text-orange-600 border-orange-200' :
                                    'bg-yellow-50 text-yellow-600 border-yellow-200'
                                  }`}>
                                    {suggestion.severity || 'Medium'}
                                  </Badge>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Location Candidates Section (Epic 2 Weighted Matching) */}
                          {suggestion.locationSuggestions && suggestion.locationSuggestions.length > 0 && !message.hideSuggestions && (() => {
                            // Rules 43-44: Filter to only show locations that exist in the database
                            const validLocationSuggestions = suggestion.locationSuggestions.filter(loc =>
                              loc.locationId && locations.some(l => l.id === loc.locationId)
                            );
                            return validLocationSuggestions.length > 0;
                          })() && (
                            <div className="space-y-2 mb-4">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Matched Locations</p>
                              {suggestion.locationSuggestions.filter(loc =>
                                // Rules 43-44: Only render locations that exist in the database
                                loc.locationId && locations.some(l => l.id === loc.locationId)
                              ).map((loc, i) => {
                                const isPreviewing = previewLocationId === loc.locationId;
                                const locationData = locations.find(l => l.id === loc.locationId);
                                const hasGps = locationData?.gps_latitude && locationData?.gps_longitude;
                                const displayPath = loc.displayPath || (locationData ? buildLocationDisplayPath(locationData) : '');
                                const mediaPreview = getLocationMediaPreview(locationData);
                                const description = loc.description || locationData?.description;

                                return (
                                  <div 
                                    key={i} 
                                    className={`bg-white border border-slate-200 rounded-xl p-3 shadow-sm hover:border-violet-300 transition-all cursor-pointer group relative overflow-hidden ${isPreviewing ? 'ring-1 ring-blue-400' : ''}`}
                                    onClick={() => handleApplySuggestion({ 
                                      ...suggestion, 
                                      location_id: loc.locationId, 
                                      location_match: displayPath 
                                    })}
                                  >
                                    <div className="flex justify-between items-start mb-1.5 gap-2">
                                      <span className="text-[11px] font-semibold text-slate-700 flex items-start pr-8 flex-1 min-w-0">
                                        {mediaPreview ? (
                                          <AuthenticatedMedia as="img" src={mediaPreview} alt="" className="w-8 h-8 rounded-md object-cover border border-slate-200 mr-2 shrink-0" />
                                        ) : (
                                          <MapPin className="h-3 w-3 mr-1.5 text-violet-500 mt-0.5 shrink-0" />
                                        )}
                                        <span className="min-w-0">
                                          <span className="block truncate">{displayPath}</span>
                                          {loc.nodeType && (
                                            <span className="text-[8px] text-slate-400 uppercase">{loc.nodeType}</span>
                                          )}
                                        </span>
                                      </span>
                                      <div className="flex items-center gap-1.5">
                                        {hasGps && (
                                          <button 
                                            className={`p-1 rounded-md transition-colors ${isPreviewing ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-400 hover:bg-blue-100 hover:text-blue-600'}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPreviewLocationId(isPreviewing ? null : loc.locationId);
                                            }}
                                            title="View on Map"
                                          >
                                            <Eye className="h-3 w-3" />
                                          </button>
                                        )}
                                        <Badge variant="outline" className={`text-[8px] uppercase px-1.5 h-4 shrink-0 ${
                                          loc.confidence === 'High' ? 'bg-green-50 text-green-600 border-green-200' :
                                          loc.confidence === 'Medium' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                          'bg-slate-50 text-slate-500 border-slate-200'
                                        }`}>
                                          {loc.confidence}
                                        </Badge>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {loc.evidence && loc.evidence.map((ev, ei) => (
                                        <span key={ei} className="text-[8px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full border border-slate-200/50 flex items-center">
                                          <CheckCircle2 className="h-2 w-2 mr-1 text-slate-400" />
                                          {ev}
                                        </span>
                                      ))}
                                    </div>
                                    {description && (
                                      <p className="text-[9px] text-slate-500 mt-1.5 line-clamp-2">{description}</p>
                                    )}
                                    {(loc.hasMedia || (locationData?.attachments?.length > 0)) && !mediaPreview && (
                                      <p className="text-[8px] text-slate-400 mt-1 flex items-center gap-1">
                                        <ImageIcon className="h-2.5 w-2.5" />
                                        Reference media available
                                      </p>
                                    )}

                                    {/* Inline Map Preview */}
                                    {isPreviewing && hasGps && (
                                      <div className="mt-3 rounded-lg overflow-hidden border border-blue-200 shadow-inner animate-in fade-in zoom-in-95 duration-200">
                                        <MyGoogleMap 
                                          containerStyle={{ width: '100%', height: '120px' }}
                                          center={{ lat: Number(locationData.gps_latitude), lng: Number(locationData.gps_longitude) }}
                                          markerPins={[{ lat: Number(locationData.gps_latitude), lng: Number(locationData.gps_longitude) }]}
                                        />
                                        <div className="bg-blue-50 p-1 text-[9px] text-blue-700 flex items-center justify-center gap-1">
                                          <MapIcon className="h-2.5 w-2.5" />
                                          GPS: {Number(locationData.gps_latitude).toFixed(4)}, {Number(locationData.gps_longitude).toFixed(4)}
                                        </div>
                                      </div>
                                    )}

                                    <div className="mt-2 text-[9px] text-violet-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                                      Apply this location <ChevronRight className="h-2.5 w-2.5 ml-0.5" />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                           {/* Cannot Match / Guided Narrowing Suggestion */}
                           {suggestion.cannotMatch && !guidedSearch.active && (
                             <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl animate-in zoom-in-95 duration-300">
                               <div className="flex items-start gap-3">
                                 <div className="p-2 bg-amber-100 rounded-lg">
                                   <Search className="h-4 w-4 text-amber-600" />
                                 </div>
                                 <div className="flex-1">
                                   <p className="text-sm font-bold text-amber-900 mb-1">I'm having trouble pinpointing your location</p>
                                   <p className="text-xs text-amber-700 leading-relaxed mb-3">
                                     I couldn't find a high-confidence match. Would you like to use the guided location assistant?
                                   </p>
                                   <Button 
                                     size="sm" 
                                     className="bg-amber-600 hover:bg-amber-700 text-white border-0 h-8 text-[11px] font-bold"
                                     onClick={() => setGuidedSearch(prev => ({ ...prev, active: true, selectedParentId: null, breadcrumb: [] }))}
                                   >
                                     Start Guided Search
                                     <ChevronRight className="h-3 w-3 ml-1" />
                                   </Button>
                                 </div>
                               </div>
                             </div>
                           )}

                          {suggestion.human_summary && (
                            <div className="p-2 bg-violet-50/50 rounded-lg border border-violet-100/50 mb-3">
                              <p className="text-[10px] text-violet-700 leading-relaxed">
                                <span className="font-bold">AI Note:</span> {suggestion.human_summary}
                              </p>
                            </div>
                          )}

                          {/* Apply Classification — Rules 18-20 + Rule 42: button is ALWAYS rendered
                              when a classification is present, regardless of whether location suggestions
                              are also shown. The recommendation and Apply action are atomic. */}
                          {!!(suggestion.incident_type && suggestion.type_id) && (
                            <Button
                              onClick={() => handleApplySuggestion(suggestion)}
                              className="w-full text-white text-xs h-8 rounded-lg shadow-sm bg-violet-600 hover:bg-violet-700"
                            >
                              <Sparkles className="w-3 h-3 mr-1.5" />
                              Apply Classification
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] mt-1 text-slate-400 px-1 ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                      {message.timestamp}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <GuidedLocationPicker
            locations={safeLocations}
            guidedSearch={guidedSearch}
            setGuidedSearch={setGuidedSearch}
            onApplyLocation={handleApplySuggestion}
            onNavigateToCreateLocation={onNavigateToCreateLocation}
            className="mx-2"
          />
          {isTyping && (
            <div className="flex justify-start">
              <div className="flex items-center space-x-2 bg-white border border-slate-200 px-4 py-2.5 rounded-2xl rounded-tl-none shadow-sm">
                <Loader2 className="h-4 w-4 text-violet-500 animate-spin" />
                <span className="text-xs text-slate-500 font-medium">AI is thinking...</span>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Offline Mode Indicator (Point 10) */}
      {isOffline && (
        <div className="mx-4 mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-center space-x-2">
          <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
          <span className="text-[10px] font-medium text-amber-700">You are currently offline. AI features are restricted.</span>
        </div>
      )}

      {/* Attachments Preview Area (Point 2) */}
      {imageAttachments.length > 0 && (
        <div className="px-4 py-3 bg-violet-50 border-t border-violet-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-violet-700 uppercase tracking-wider flex items-center">
              <ImageIcon className="w-3 h-3 mr-1" />
              Report Attachments ({imageAttachments.length})
            </span>
            {lastAnalyzedCount < imageAttachments.length && (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleAnalyzeImages}
                disabled={isTyping || isOffline}
                className="h-6 text-[10px] bg-violet-600 text-white hover:bg-violet-700 px-2 rounded-full flex items-center"
              >
                <Sparkles className="w-3 h-3 mr-1.5" />
                Analyze Photos
              </Button>
            )}
            {lastAnalyzedCount >= imageAttachments.length && imageAttachments.length > 0 && (
              <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-200">
                Analyzed
              </Badge>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {imageAttachments.map((file, idx) => (
              <div key={idx} className="relative group flex-shrink-0">
                <img 
                  src={URL.createObjectURL(file)} 
                  alt="Attachment" 
                  className="w-12 h-12 object-cover rounded-lg border border-violet-200 shadow-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSend();
                setInput('');
              }
            }}
            placeholder={isOffline ? "Waiting for connection..." : "Type your message..."}
            disabled={isOffline || isTyping}
            className="min-w-0 flex-1 rounded-full border-slate-200 bg-slate-50/50 px-5 py-5 focus-visible:border-violet-500 focus-visible:ring-violet-500"
          />
          <button
            type="button"
            onClick={() => {
              handleSend();
              setInput('');
            }}
            disabled={(!input.trim() && imageAttachments.length === 0) || isTyping || isOffline}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white transition-all hover:brightness-110 active:scale-95 disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)', boxShadow: '0 6px 18px rgba(124,58,237,0.35)' }}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[11px] text-center text-slate-400 mt-3">
          AI can make mistakes. Please verify important information.
        </p>
      </div>
    </div>
  );
};

export default AIChatPanel;
